import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { skillstreeApi } from '../services/api';
import { dashboardService } from '@/services/dashboardService';
import { getUserRole, getUserBadge } from '@/utils/navigation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import './CategoryDetailPage.css';

// Define types for better TypeScript support
interface MicroskillModule {
  code: string;
  description: string;
}

interface SkillstreeModule {
  id: number;
  name: string;
  description: string;
  duration: string;
  image_url?: string | null;
  modules: MicroskillModule[];
  is_enrolled?: boolean;
  can_enroll?: boolean;
  is_completed?: boolean;
  learning_progress?: number;
}

interface Category {
  id: string;
  title: string;
  description: string;
  image: string;
  skillstrees: SkillstreeModule[];
}

const CategoryDetailPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  
  // State for API data
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState<{ [key: number]: boolean }>({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [pendingEnrollId, setPendingEnrollId] = useState<number | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [activeEnrollments, setActiveEnrollments] = useState<number>(0);
  const [maxSlots, setMaxSlots] = useState<number>(3); // Default to 3 for Plus users, will be updated based on actual role
  const [isPlus, setIsPlus] = useState<boolean>(false);

  // Fetch category data from API
  const fetchCategory = async () => {
    if (!categoryId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, get the category list to find the slug from ID
      const categoriesResponse = await skillstreeApi.getCoreCategories();
      const categoriesData = (categoriesResponse.data as any).data; // API returns { status, data: [...], message }

      if (!categoriesData || !Array.isArray(categoriesData)) {
        throw new Error('Failed to fetch categories');
      }

      // Find the category by ID to get its slug
      const categoryIdNum = parseInt(categoryId, 10);
      const category = categoriesData.find((cat: any) => cat.id === categoryIdNum);

      if (!category || !category.slug) {
        throw new Error('Category not found');
      }

      // Now fetch skillstrees using the slug
      const response = await skillstreeApi.getCoreCategoryWithSkills(category.slug);
      const apiData = (response.data as any).data; // API returns { status, data: {...}, message }

      if (!apiData || !apiData.skillstrees) {
        throw new Error('No skillstrees found for this category');
      }

      // Map API response to component structure
      const skillstrees: SkillstreeModule[] = apiData.skillstrees.map((st: any) => {
        // Get the first version's micro_skills to build modules list
        const firstVersion = st.versions && st.versions.length > 0 ? st.versions[0] : null;
        const microSkills = firstVersion?.micro_skills || [];

        // Build modules array using unique_code and title
        const modules: MicroskillModule[] = microSkills.map((ms: any) => {
          return {
            code: ms.unique_code || '',
            description: ms.title || '',
          };
        });

        // Calculate duration in hours from total duration (seconds)
        const totalHours = st.duration ? Math.round(st.duration / 3600 * 10) / 10 : 0;
        const durationText = totalHours > 0 ? `${totalHours} hours - Beginner` : 'Beginner';

        return {
          id: st.skillstree_id,
          name: st.skillstree_name,
          description: st.description || '',
          duration: durationText,
          image_url: st.image_url || null,
          modules: modules,
          can_enroll: st.can_enroll,
          is_enrolled: st.is_enrolled,
        };
      });

      // Fetch enrollment status and completion status from /v1/skillstrees API
      try {
        const skillstreesResponse = await skillstreeApi.getSkillstrees();
        const skillstreesData = (skillstreesResponse.data as any).data || [];
        
        // Create maps of skillstree_id to enrollment and completion status
        const enrollmentMap = new Map<number, boolean>();
        const completionMap = new Map<number, { is_completed: boolean; learning_progress: number }>();
        skillstreesData.forEach((st: any) => {
          if (st.skillstree_id) {
            enrollmentMap.set(st.skillstree_id, st.is_enrolled || false);
            // Check completion status: either is_completed flag or learning_progress === 100
            const learningProgress = st.versions?.[0]?.learning_progress || 0;
            const isCompleted = st.is_completed === true || learningProgress === 100;
            completionMap.set(st.skillstree_id, {
              is_completed: isCompleted,
              learning_progress: learningProgress
            });
          }
        });

        // Update enrollment and completion status for skillstrees
        skillstrees.forEach((st) => {
          st.is_enrolled = enrollmentMap.get(st.id) || false;
          const completionData = completionMap.get(st.id);
          if (completionData) {
            st.is_completed = completionData.is_completed;
            st.learning_progress = completionData.learning_progress;
          }
        });
      } catch (enrollmentErr) {
        console.error('Error fetching enrollment status:', enrollmentErr);
        // Continue without enrollment status if API fails
      }

      // Create category object
      const categoryData: Category = {
        id: categoryId,
        title: apiData.core_category_name || category.name || 'Category',
        description: category.description || '',
        image: '/big_data.jpg', // Image URL not in API response, using placeholder
        skillstrees: skillstrees,
      };

      setCategory(categoryData);
    } catch (err) {
      console.error('Error fetching category:', err);
      setError(err instanceof Error ? err.message : 'Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkSubscriptionAndEnrollments = async () => {
      try {
        const profileResponse = await dashboardService.getUserProfile();
        const profileData = (profileResponse?.data as any)?.data as any;
        const subscription = profileData?.subscription || profileData?.data?.subscription;
        
        // Check if subscription exists and is not expired
        let hasActiveSubscription = false;
        if (subscription) {
          // Check if subscription has expire_date and if it's in the future
          if (subscription.expire_date) {
            const expireDate = new Date(subscription.expire_date);
            const now = new Date();
            hasActiveSubscription = expireDate > now;
          } else if (subscription.is_active !== undefined) {
            // Use is_active flag if available
            hasActiveSubscription = subscription.is_active === true;
          } else {
            // Fallback: if subscription exists but no expiration/active info, assume active
            // (This handles cases where API only returns active subscriptions)
            hasActiveSubscription = true;
          }
        }
        
        setHasSubscription(hasActiveSubscription);

        // Calculate active (non-completed) enrollments
        const profileSkillstrees = profileData?.skillstrees as any;
        const enrolledSkillstrees = profileSkillstrees?.enrolled || [];
        const activeEnrollmentsCount = enrolledSkillstrees.filter((st: any) => !st.is_completed).length;
        setActiveEnrollments(activeEnrollmentsCount);

        // Determine max slots based on subscription and role
        const userRole = getUserRole(profileData.user?.role);
        const isPlusUser = getUserBadge(userRole) === 'PLUS';
        setIsPlus(isPlusUser);
        let maxSlotsValue = 1; // Default to 1 slot
        
        // Set max slots based on subscription and role
        if (hasActiveSubscription && userRole.id > 1 && userRole.id < 5) {
          maxSlotsValue = isPlusUser ? 3 : 1;
        } else if (!hasActiveSubscription) {
          // Free users without subscription get 1 slot
          maxSlotsValue = 1;
        }
        
        setMaxSlots(maxSlotsValue);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        setHasSubscription(false);
        setActiveEnrollments(0);
        setMaxSlots(1);
      }
    };

    fetchCategory();
    checkSubscriptionAndEnrollments();
  }, [categoryId]);

  // Handle enroll button click - show confirmation dialog or redirect to subscription
  const handleEnroll = (skillstreeId: number, e: React.MouseEvent) => {
    e.stopPropagation();

    //If user has no subscription and no active enrollments, redirect to subscription page
    if(!hasSubscription && !activeEnrollments) {
      navigate('/profile?tab=subscription');
      return;
    }
    
    // If user is free plan and already enrolled in 1+ skillstree, redirect to subscription page
    if (!hasSubscription && activeEnrollments >= 1) {
      navigate('/profile?tab=subscription');
      return;
    }

    // If user has subscription but reached slot limit
    if (hasSubscription && activeEnrollments >= maxSlots) {
      // For Plus users, show toast message instead of redirecting
      if (isPlus) {
        toast.info('Complete any skillstree before enrolling in a new skillstree', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
      // For non-Plus subscribed users, redirect to subscription page
      navigate('/profile?tab=subscription');
      return;
    }
    
    // Otherwise, show confirmation dialog
    setPendingEnrollId(skillstreeId);
    setConfirmDialogOpen(true);
  };

  // Confirm enrollment after user confirms
  const confirmEnroll = async () => {
    if (pendingEnrollId === null) return;

    try {
      setEnrolling(prev => ({ ...prev, [pendingEnrollId]: true }));
      setConfirmDialogOpen(false);
      await skillstreeApi.enroll(pendingEnrollId);
      
      // Fetch updated enrollment status from /v1/skillstrees API
      try {
        const skillstreesResponse = await skillstreeApi.getSkillstrees();
        const skillstreesData = (skillstreesResponse.data as any).data || [];
        
        // Create a map of skillstree_id to is_enrolled status
        const enrollmentMap = new Map<number, boolean>();
        skillstreesData.forEach((st: any) => {
          if (st.skillstree_id) {
            enrollmentMap.set(st.skillstree_id, st.is_enrolled || false);
          }
        });

        // Update the enrollment status in the category state from API
        setCategory(prev => {
          if (!prev) return null;
          return {
            ...prev,
            skillstrees: prev.skillstrees.map(st => ({
              ...st,
              is_enrolled: enrollmentMap.get(st.id) || false
            }))
          };
        });
      } catch (enrollmentErr) {
        console.error('Error fetching updated enrollment status:', enrollmentErr);
        // Fallback: update local state if API fails
        setCategory(prev => {
          if (!prev) return null;
          return {
            ...prev,
            skillstrees: prev.skillstrees.map(st => 
              st.id === pendingEnrollId 
                ? { ...st, is_enrolled: true }
                : st
            )
          };
        });
      }

      // Refresh active enrollments count after successful enrollment
      try {
        const profileResponse = await dashboardService.getUserProfile();
        const profileData = (profileResponse?.data as any)?.data as any;
        const profileSkillstrees = profileData?.skillstrees as any;
        const enrolledSkillstrees = profileSkillstrees?.enrolled || [];
        const activeEnrollmentsCount = enrolledSkillstrees.filter((st: any) => !st.is_completed).length;
        setActiveEnrollments(activeEnrollmentsCount);
      } catch (error) {
        console.error('Failed to refresh enrollment count:', error);
        // Increment count optimistically if API fails
        setActiveEnrollments(prev => prev + 1);
      }
      
      toast.success('Successfully enrolled in skillstree!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Re-fetch category data to get updated can_enroll status for all skillstrees
      await fetchCategory();
    } catch (err: any) {
      console.error('Error enrolling in skillstree:', err);
      console.error('Error response:', err?.response?.data);
      
      // Extract error message from various possible locations
      const errorMessage = 
        err?.response?.data?.message || 
        err?.response?.data?.error || 
        err?.message || 
        'Failed to enroll. Please try again.';
      
      // Check if user is already enrolled
      if (errorMessage.includes('You are already enrolled in this Skillstree')) {
        toast.info(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } finally {
      setEnrolling(prev => ({ ...prev, [pendingEnrollId]: false }));
      setPendingEnrollId(null);
    }
  };

  // Cancel enrollment
  const cancelEnroll = () => {
    setConfirmDialogOpen(false);
    setPendingEnrollId(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  // Error state
  if (error || !category) {
    return (
      <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold">{error || 'Category Not Found'}</h1>
          <button 
            onClick={() => navigate('/skillstree')}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded text-sm sm:text-base"
          >
            Back to Categories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-saas-black text-white p-4 sm:p-6 relative pb-24">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Breadcrumbs */}
        <div className="mb-4 sm:mb-6">
          <nav className="breadcrumbs-container">
            <img 
              src="/breadcrumb_icon.svg" 
              alt="Dashboard" 
              className="breadcrumb-icon"
            />
            <span className="breadcrumb-separator"> {'>'} </span>
            <button 
              onClick={() => navigate('/skillstree')}
              className="breadcrumb-link"
            >
              Core Categories
            </button>
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text">{category.title}</span>
            <span className="breadcrumb-separator"> {'>'} </span>
            <span className="breadcrumb-text breadcrumb-active">Skillstree</span>
          </nav>
        </div>

        {/* Header Section */}
        <div className="mb-5 text-center">
          {/* Category Badge with Blue Glow */}
          <div className="flex mb-4 sm:mb-6">
            <div className="relative category-badge-container">
              {/* Blue Glow Outer Ring */}
              <div className="absolute inset-0 rounded-full category-badge-glow" />
              
              {/* Inner Badge Circle */}
              <div className="absolute inset-4 rounded-full flex items-center justify-center category-badge-inner">
                <h1 className="text-xl font-bold text-white uppercase">
                  {category.title}
                </h1>
              </div>
            </div>
          </div>

          {/* Category Description */}
          <p className="text-white text-base sm:text-lg mb-6 sm:mb-8 text-left max-w-[924px]">
            {category.description}
          </p>

          {/* Section Title */}
          <h2 className="text-xl sm:text-2xl font-bold text-left">Choose Your Skillstree:</h2>
        </div>

        {/* Skillstrees Section */}
        {category.skillstrees && category.skillstrees.length > 0 ? (
          <div className="space-y-4 sm:space-y-6">
            {category.skillstrees.map((skillstree) => (
              <div
                key={skillstree.id}
                className="relative skillstree-card-container"
              >
                {/* Front Box - Card content */}
                <div className="skillstree-card-front">
                  <div className="flex items-center gap-6 sm:gap-12 relative">
                    {/* Badge Box */}
                    <div className="flex-shrink-0 skillstree-badge-box">
                      <img 
                        src={skillstree.image_url || ''} 
                        alt={skillstree.name}
                        className="skillstree-badge-image"
                      />
                    </div>

                    {/* Skill Circles - Horizontal progression */}
                    <div className="flex-1 flex flex-col skillstree-circles-wrapper">
                      {/* Text above circles */}
                      <div className="skillstree-text-above">
                        <div className="skillstree-description-name">{skillstree.name}</div>
                        <div className="skillstree-description-text text-gray-400 text-sm">{skillstree.description}</div>
                        <div className="skillstree-duration-text">{skillstree.duration.replace(' - ', ' · ')}</div>
                      </div>
                      
                      {/* Circles container */}
                      <div className="flex items-center skillstree-circles-container">
                        {skillstree.modules && skillstree.modules.length > 0 ? (
                          skillstree.modules.map((module, index) => {
                            const isFirstThree = index < 3;
                            const isLast = index === skillstree.modules.length - 1;
                            
                            return (
                              <div key={index} className="flex items-center">
                                {/* Circle with glow */}
                                <div 
                                  className={`skillstree-circle ${isFirstThree ? 'skillstree-circle-orange' : 'skillstree-circle-green'}`}
                                  title={`${module.code}: ${module.description}`}
                                >
                                  <div className="skillstree-circle-content">
                                    <div className="skillstree-circle-text">
                                      <div className="skillstree-circle-code">{module.code}</div>
                                      <div className="skillstree-circle-description">{module.description}</div>
                                    </div>
                                  </div>
                                </div>
                                {/* Connecting line */}
                                {!isLast && <div className="skillstree-circle-connector" />}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-white text-sm">No modules available</div>
                        )}
                      </div>
                    </div>

                    {/* Buttons Container - Top Right */}
                    <div className="skillstree-buttons-container">
                      <button
                        className="skillstree-select-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Allow access if user has subscription OR if skillstree is completed
                          const canAccess = hasSubscription || skillstree.is_completed === true;
                          if (!canAccess) {
                            navigate('/profile?tab=subscription');
                          } else {
                            navigate(`/skillstree/${categoryId}/${skillstree.id}`);
                          }
                        }}
                      >
                        Select
                      </button>
                      <button
                        className={`skillstree-enroll-button ${
                          hasSubscription && isPlus && activeEnrollments >= maxSlots && !skillstree.is_enrolled
                            ? 'skillstree-enroll-button-disabled-look'
                            : ''
                        }`}
                        onClick={(e) => handleEnroll(skillstree.id, e)}
                        disabled={
                          enrolling[skillstree.id] || 
                          skillstree.is_enrolled
                        }
                        style={{
                          cursor: hasSubscription && isPlus && activeEnrollments >= maxSlots && !skillstree.is_enrolled
                            ? 'not-allowed'
                            : undefined
                        }}
                        title={
                          skillstree.is_enrolled
                            ? 'Enrolled'
                            : enrolling[skillstree.id]
                            ? 'Enrolling...'
                            : (hasSubscription && isPlus && activeEnrollments >= maxSlots)
                            ? 'Complete any skillstree before enrolling in a new skillstree'
                            : ''
                        }
                      >
                        {enrolling[skillstree.id]
                          ? 'Enrolling...'
                          : skillstree.is_enrolled
                            ? 'Enrolled'
                            : 'Enroll'
                        }
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-white text-lg text-center py-12">
            <p>No skillstrees available for this category yet.</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="bg-neutral-800 border-neutral-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Enrollment</DialogTitle>
            <DialogDescription className="text-neutral-300">
              Are you sure you want to enroll in this skillstree? This action will add it to your learning path.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              onClick={cancelEnroll}
              className="px-4 py-2 bg-neutral-600 hover:bg-neutral-500 text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmEnroll}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryDetailPage;

